<script>
import ContactEntry from './contact-entry.vue'
import ContactNavbar from './contact-navbar.vue'
import PaginationButtons from '../pagination-buttons.vue'
import Toast from '../lib/toast.vue'

export default {
  components: {ContactEntry, ContactNavbar, PaginationButtons, Toast},

  data() {
    return {
      contacts: [],
      numContacts: 0,
      page: 1,
      numPages: 0,
      search: '',
      observed: this.$storage.observed,
      // null = follow the default (collapsed when more than 3); a boolean once
      // the user toggled the cluster explicitly.
      inboxOpen: null,
      subscribedOpen: null,
      toastMessage: '',
    }
  },

  computed: {
    contactIds() { return this.observed.contactIds },
    pendingConnections() { return this.observed.pendingConnections },
    exhibitorInbox() { return this.observed.exhibitorInbox },
    subscribedChannels() { return this.observed.subscribedChannels },
    inboxExpanded() {
      // Default open only with few entries AND actual new messages; otherwise
      // collapsed. An explicit toggle (inboxOpen !== null) always wins.
      if (this.inboxOpen !== null) return this.inboxOpen
      return this.exhibitorInbox.length <= 3 && this.inboxUnreadTotal > 0
    },
    subscribedExpanded() {
      if (this.subscribedOpen !== null) return this.subscribedOpen
      return this.subscribedChannels.length <= 3 && this.subscribedUnreadTotal > 0
    },
    inboxUnreadTotal() {
      return this.exhibitorInbox.reduce((sum, c) => sum + (c.unread_count || 0), 0)
    },
    subscribedUnreadTotal() {
      return this.subscribedChannels.reduce((sum, c) => sum + (c.numUnreadMessages || 0), 0)
    },
  },

  watch: {
    'observed.contactIds': function(newVal) {
      if (newVal.length > 0) this.fetchContacts()
    },
    // Incoming direct message (websocket 'posted') - refetch so the server
    // ordering (has_unread, last_post_at) is reflected while on this view.
    'observed.directMessageCounter': function() {
      this.fetchContacts(this.page)
    },
  },

  created() {
    if (this.contactIds.length) this.fetchContacts()
    // Aussteller-Postfach + abonnierte Kanäle kommen reaktiv aus dem Storage.
    this.$storage.fetchExhibitorInbox()
  },

  methods: {
    // Exhibitor.__str__ format: "ZPE26, ZPS26 - ACME GmbH".
    withEvents(name, shortNames) {
      return (shortNames && shortNames.length)
        ? `${shortNames.join(', ')} - ${name}` : name
    },
    introPreview(message) {
      // Truncate the invitation text for the request list, preferably at a
      // word boundary, and mark the cut with an ellipsis.
      const limit = 120
      if (!message || message.length <= limit) return message
      let cut = message.slice(0, limit)
      let lastSpace = cut.lastIndexOf(' ')
      if (lastSpace > 80) cut = cut.slice(0, lastSpace)
      return cut.trimEnd() + '…'
    },

    contactUrl(contact) {
      let channel = this.$storage.getDirectChannel(contact.uuid)
      if (channel) return `/community/${contact.uuid}/${channel.id}`
      return `/community/${contact.uuid}`
    },

    onSearchChanged: function (search) {
      this.search = search
      this.fetchContacts(1)
    },

    onSearchDelete: function () {
      this.search = ''
      this.fetchContacts(1)
    },

    fetchContacts: function (page = 1) {
      let payload = { uuids: this.contactIds }
      if (this.search) payload.search = this.search
      this.$api.post(`/community-users/contacts/?page=${page}`, payload).then(response => {
        this.contacts = response.data.results
        this.numContacts = response.data.count
        this.numPages = response.data.pages
        this.page = page
      })
    },

    toggleInbox() { this.inboxOpen = !this.inboxExpanded },
    toggleSubscribed() { this.subscribedOpen = !this.subscribedExpanded },

    showActionError(error) {
      let data = error && error.response && error.response.data
      this.toastMessage = (data && data.error) || 'Aktion fehlgeschlagen.'
    },

    acceptConnection(req) {
      this.$chat.post(`connections/${req.channel_id}/accept/`).then(() => {
        this.$storage.fetchPendingConnections()
        this.$storage.fetchContactsForUser()
        this.$router.push(`/community/${req.requester.uuid}/${req.channel_id}`)
      }).catch(error => this.showActionError(error))
    },
    declineConnection(req) {
      if (!confirm(`Anfrage von ${req.requester.name} ablehnen?`)) return
      this.$chat.post(`connections/${req.channel_id}/decline/`)
        .then(() => this.$storage.fetchPendingConnections())
        .catch(error => this.showActionError(error))
    },
    blockConnection(req) {
      if (!confirm(`${req.requester.name} blockieren? Ein bestehender Verlauf wird gelöscht.`)) return
      this.$chat.post(`connections/${req.channel_id}/block/`)
        .then(() => this.$storage.fetchPendingConnections())
        .catch(error => this.showActionError(error))
    },
  },
}
</script>

<template>

  <div>
    <contact-navbar class="mb-1"
      :search="search"
      @search="onSearchChanged($event)"
      @delete-search="onSearchDelete"
    />

    <div v-if="pendingConnections.length" class="mt-1 mb-2">
      <div class="c-cluster-header">
        Verbindungsanfragen ({{ pendingConnections.length }})
      </div>
      <div class="list-group">
        <div v-for="req in pendingConnections" :key="'req-' + req.channel_id"
         class="list-group-item d-flex align-items-center">
          <img v-if="req.requester.image" :src="req.requester.image"
           class="c-req-avatar rounded-circle object-fit-cover me-2" />
          <div class="flex-fill">
            <div class="fw-bold">{{ req.requester.name }}</div>
            <small class="text-muted">möchte sich verbinden</small>
            <div v-if="req.message" class="small text-muted fst-italic">
              „{{ introPreview(req.message) }}“
            </div>
          </div>
          <button class="btn btn-sm btn-success me-1" title="Annehmen"
           @click.prevent="acceptConnection(req)"><i class="fas fa-check"></i></button>
          <button class="btn btn-sm btn-outline-secondary me-1" title="Ablehnen"
           @click.prevent="declineConnection(req)"><i class="fas fa-times"></i></button>
          <button class="btn btn-sm btn-outline-danger" title="Blockieren"
           @click.prevent="blockConnection(req)"><i class="fas fa-ban"></i></button>
        </div>
      </div>
    </div>

    <div v-if="exhibitorInbox.length" class="mt-1 mb-2">
      <div class="c-cluster-header d-flex justify-content-between align-items-center" @click="toggleInbox">
        <span>Aussteller-Postfächer ({{ exhibitorInbox.length }})</span>
        <span>
          <span v-if="inboxUnreadTotal" class="badge bg-danger me-1" title="neue Nachrichten">
            <i class="far fa-envelope"></i> {{ inboxUnreadTotal }}
          </span>
          <i class="fas" :class="inboxExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
        </span>
      </div>
      <div v-if="inboxExpanded" class="list-group">
        <router-link v-for="c in exhibitorInbox" :key="'inbox-' + c.company_id"
         :to="`/exhibitor-inbox/${c.company_id}`" class="text-deco-none">
          <div class="list-group-item list-group-item-action d-flex align-items-center">
            <i class="fas fa-inbox me-2 text-muted"></i>
            <div class="flex-fill">
              <div class="fw-bold">Aussteller-Postfach: {{ withEvents(c.name, c.event_short_names) }}</div>
            </div>
            <span v-if="c.unread_count" class="badge bg-danger" title="neue Nachrichten">
              <i class="far fa-envelope"></i> {{ c.unread_count }}
            </span>
          </div>
        </router-link>
      </div>
    </div>

    <div v-if="subscribedChannels.length" class="mt-1 mb-2">
      <div class="c-cluster-header d-flex justify-content-between align-items-center" @click="toggleSubscribed">
        <span>Abonnierte öffentliche Kanäle ({{ subscribedChannels.length }})</span>
        <span>
          <span v-if="subscribedUnreadTotal" class="badge bg-danger me-1">
            <i class="far fa-envelope"></i> {{ subscribedUnreadTotal }}
          </span>
          <i class="fas" :class="subscribedExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
        </span>
      </div>
      <div v-if="subscribedExpanded" class="list-group">
        <router-link v-for="ch in subscribedChannels" :key="'sub-' + ch.id"
         :to="`/channel-${ch.name}`" class="text-deco-none">
          <div class="list-group-item list-group-item-action d-flex align-items-center">
            <i class="fas fa-bullhorn me-2 text-muted"></i>
            <div class="flex-fill">
              <div class="fw-bold">{{ ch.display_name }}</div>
              <small v-if="ch.header" class="text-muted text-truncate d-block">{{ ch.header }}</small>
            </div>
            <span v-if="ch.numUnreadMessages" class="badge bg-danger">
              <i class="far fa-envelope"></i> {{ ch.numUnreadMessages }}
            </span>
          </div>
        </router-link>
      </div>
    </div>

    <div v-if="numContacts > 0">
      <small class="text-muted me-1">{{numContacts}} Treffer</small>

      <div class="list-group mt-1">
        <router-link v-for="contact in contacts" :key="contact.id"
         :to="contactUrl(contact)" class="text-deco-none">
          <contact-entry :contact="contact" />
        </router-link>
      </div>

      <pagination-buttons :page.sync="page" :num-pages="numPages"
        @update:page="fetchContacts($event)" />

    </div>
    <div v-else>
      <div class="text-center mt-5">-- keine Treffer --</div>
    </div>

    <toast v-if="toastMessage" :message="toastMessage" type="error" @closed="toastMessage = ''" />
  </div>
</template>

<style scoped>
.text-deco-none {
  text-decoration: none;
}
/* Clickable header for the collapsible message clusters. */
.c-cluster-header {
  cursor: pointer;
  background-color: var(--bs-light, #f8f9fa);
  color: #495057;
  font-weight: 600;
  font-size: .85rem;
  padding: .4rem .75rem;
  border-radius: .25rem;
}
/* Fixed avatar size - Bootstrap has no pixel-based sizing utilities. */
.c-req-avatar {
  height: 34px;
  width: 34px;
}
</style>
